import { shapes } from '@joint/plus';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Alert, Autocomplete, Box, CircularProgress, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import React, { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import * as BusinessHoursService from '../../../../../../../../services/2talk/business-hours.service.js';
import { useBaseInspector } from './useBaseInspector';

/**
 * BusinessHoursInspector — Item #3 roadmap chatbot JOINT (07/10/2026).
 *
 * Caixa SÍNCRONA que avalia expediente + feriados do hirer e decide por qual porta
 * ("Aberto" / "Fechado") o fluxo segue. Admin só precisa:
 *   1. Escolher o expediente (tb_work_hour) no dropdown — alimentado por GET /business-hours/work-hours
 *   2. Opcional: sobrescrever timezone (default UTC-3 BR via Library.dateTime no backend)
 *
 * Portas "Aberto" e "Fechado" são FIXAS e nascem com a caixa (shape overrides.initialize),
 * com IDs persistidos em attrs/openPortId/text e attrs/closedPortId/text. O backend lê esses
 * IDs e segue a porta certa — admin não configura isso.
 *
 * Loading UX pro Autocomplete reusa o padrão aplicado no SetVariable (spinner inline, nunca
 * pisca erro durante fetch).
 */

interface Props {
    cell: shapes.app.Base;
    chatbotFlowId: string;
}

type WorkHourOption = { id: number; name: string };

const cellProps = {
    label: ['attrs', 'label', 'text'],
    workHourId: ['attrs', 'workHourId', 'text'],
    timezone: ['attrs', 'timezone', 'text'],
};

// Lista curta de timezones comuns — admin brasileiro usa 99% UTC-3; se precisar expandir pra
// clientes de outros fusos, basta adicionar aqui ou substituir por API/Intl.supportedValuesOf('timeZone').
const COMMON_TIMEZONES = [
    { value: '',                     label: '(padrão BR — UTC-3)' },
    { value: 'America/Sao_Paulo',    label: 'America/Sao_Paulo (BR)' },
    { value: 'America/Manaus',       label: 'America/Manaus (BR Oeste)' },
    { value: 'America/Noronha',      label: 'America/Noronha (BR Atlântico)' },
    { value: 'America/New_York',     label: 'America/New_York (EST/EDT)' },
    { value: 'America/Los_Angeles',  label: 'America/Los_Angeles (PST/PDT)' },
    { value: 'Europe/Lisbon',        label: 'Europe/Lisbon (PT)' },
    { value: 'Europe/London',        label: 'Europe/London (GMT/BST)' },
    { value: 'Europe/Madrid',        label: 'Europe/Madrid (CET/CEST)' },
    { value: 'UTC',                  label: 'UTC' },
];

const BusinessHoursInspector = (props: Props): ReactElement => {
    const { cell } = props;
    const intl = useIntl();

    const [label, setLabel] = useState<string>('');
    const [workHourId, setWorkHourId] = useState<number | ''>('');
    const [timezone, setTimezone] = useState<string>('');
    const [options, setOptions] = useState<WorkHourOption[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const isSyncingRef = useRef(false);

    const loadWorkHours = useCallback(async () => {
        setLoading(true);
        try {
            const resp: any = await BusinessHoursService.getWorkHours();
            const raw: any[] = Array.isArray(resp?.data) ? resp.data : [];
            const list: WorkHourOption[] = raw
                .map((w) => ({ id: Number(w?.id ?? w?.Id ?? 0), name: String(w?.name ?? w?.Name ?? '') }))
                .filter((w) => w.id > 0 && !!w.name)
                .sort((a, b) => a.name.localeCompare(b.name));
            setOptions(list);
        } catch {
            setOptions([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadWorkHours(); }, [loadWorkHours]);

    const assignFormFields = useCallback((): void => {
        if (isSyncingRef.current) return;
        setLabel(cell.prop(cellProps.label) ?? '');
        const whRaw = cell.prop(cellProps.workHourId);
        if (whRaw != null && String(whRaw).trim() !== '') {
            const n = parseInt(String(whRaw), 10);
            if (Number.isFinite(n) && n > 0) setWorkHourId(n);
        }
        setTimezone(cell.prop(cellProps.timezone) ?? '');
    }, [cell]);

    const changeCellProp = useBaseInspector({ cell, assignFormFields });

    const persist = (path: string[], value: string) => {
        isSyncingRef.current = true;
        try {
            changeCellProp(path, value);
        } finally {
            queueMicrotask(() => { isSyncingRef.current = false; });
        }
    };

    const selectedWh = options.find((o) => o.id === workHourId) ?? null;
    const isMissing = !loading && !!workHourId && !selectedWh;

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600 }}>
                        {intl.formatMessage({ id: 'bh.title', defaultMessage: 'Horário de atendimento' })}
                    </Typography>
                    <Tooltip title={intl.formatMessage({
                        id: 'bh.title.hint',
                        defaultMessage: 'Decide o fluxo entre "Aberto" e "Fechado" com base no expediente escolhido e nos feriados cadastrados do hirer.',
                    })}>
                        <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </Tooltip>
                </Stack>

                <TextField
                    label={intl.formatMessage({ id: 'bh.label', defaultMessage: 'Rótulo (opcional)' })}
                    size="small"
                    value={label}
                    onChange={(e) => {
                        setLabel(e.target.value);
                        changeCellProp(cellProps.label, e.target.value);
                    }}
                    placeholder="Horário de atendimento"
                />

                <Autocomplete<WorkHourOption, false, false, false>
                    size="small"
                    options={options}
                    value={selectedWh}
                    loading={loading}
                    loadingText={intl.formatMessage({ id: 'bh.loading', defaultMessage: 'Carregando expedientes...' })}
                    noOptionsText={intl.formatMessage({
                        id: 'bh.empty',
                        defaultMessage: 'Nenhum expediente cadastrado — cadastre em Configurações → Expedientes.',
                    })}
                    getOptionLabel={(o) => o?.name ?? ''}
                    isOptionEqualToValue={(a, b) => a?.id === b?.id}
                    disabled={loading}
                    onChange={(_, next) => {
                        setWorkHourId(next?.id ?? '');
                        persist(cellProps.workHourId, next?.id ? String(next.id) : '');
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={intl.formatMessage({ id: 'bh.workhour', defaultMessage: 'Expediente' })}
                            placeholder={loading
                                ? intl.formatMessage({ id: 'bh.loading', defaultMessage: 'Carregando expedientes...' })
                                : intl.formatMessage({ id: 'bh.workhour.placeholder', defaultMessage: 'Escolha o expediente' })}
                            error={isMissing}
                            helperText={isMissing
                                ? intl.formatMessage({
                                    id: 'bh.workhour.missing',
                                    defaultMessage: 'Expediente não existe mais no cadastro. Escolha outro.',
                                })
                                : undefined}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loading ? <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />

                <TextField
                    select
                    label={intl.formatMessage({ id: 'bh.timezone', defaultMessage: 'Fuso horário' })}
                    size="small"
                    value={timezone}
                    onChange={(e) => {
                        setTimezone(e.target.value);
                        persist(cellProps.timezone, e.target.value);
                    }}
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                    helperText={intl.formatMessage({
                        id: 'bh.timezone.help',
                        defaultMessage: 'Default: Brasil UTC-3. Mude só se o cliente operar em outro fuso.',
                    })}
                >
                    {COMMON_TIMEZONES.map((tz) => (
                        <option key={tz.value} value={tz.value}>{tz.label}</option>
                    ))}
                </TextField>

                <Alert severity="info" sx={{ fontSize: 12, py: 0.5 }}>
                    {intl.formatMessage({
                        id: 'bh.ports.hint',
                        defaultMessage: 'A caixa tem 2 saídas fixas: "Aberto" (dentro do expediente) e "Fechado" (fora do expediente ou feriado). Conecte cada uma na próxima etapa do fluxo.',
                    })}
                </Alert>
            </Box>
        </>
    );
};

export default BusinessHoursInspector;
